import React, { useState, useEffect, useRef } from 'react';
import config from '../config';
import { useTranslation } from 'react-i18next';
import StripePaymentModal from './StripePaymentModal';
import { useNavigate } from 'react-router-dom';
import { AuthPage } from '../pages/LoginRegistrationPages';
import { getUserLevelLabel } from '../utils/userLevelHelpers';
import { useSearchParams } from 'react-router-dom';

const UserProfile = ({ showCloseButton = false }) => {
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    preferredLanguage: '',
    userLevel: 0,
    birthDate: ''
  });

  const [originalProfile, setOriginalProfile] = useState(null);
  const [userLanguage, setUserLanguage] = useState('en');
  const { t, i18n } = useTranslation();
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  const fetchUrl = config.getApiUrl('users/me');
  const updateUrl = config.getApiUrl('users/profile');
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      const loadUserFromLocalStorage = () => {
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const parsed = JSON.parse(userData);
            setProfile((prev) => ({ ...prev, ...parsed }));
            setOriginalProfile(parsed);
            if (parsed.preferredLanguage) {
              setUserLanguage(parsed.preferredLanguage);
            }
          } catch (e) {
            console.warn('Failed to parse user data from localStorage');
          }
        }
      };

      const fetchUserFromAPI = async () => {
        try {
          const res = await fetch(fetchUrl, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          const data = await res.json();

          if (res.ok && data.user) {
            setProfile(data.user);
            setOriginalProfile(data.user);
            localStorage.setItem('userData', JSON.stringify(data.user));
            if (data.user.preferredLanguage) {
              setUserLanguage(data.user.preferredLanguage);
            }
          } else {
            setMessage(data.message || t('profile.loadError', 'Failed to load profile'));
          }
        } catch (err) {
          console.error('Error fetching user:', err);
          setMessage(t('profile.serverError', 'Server error loading profile'));
        }
      };

      loadUserFromLocalStorage();
      fetchUserFromAPI();
    }
  }, [fetchUrl, token]);

  if (!token) {
    return (
      <div className="h-full overflow-hidden">
        <AuthPage initialView="login" inline />
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  const hasChanges = () => {
    if (!originalProfile) return false;
    return (
      originalProfile &&
      (
        profile.firstName !== originalProfile.firstName ||
        profile.lastName !== originalProfile.lastName ||
        profile.phoneNumber !== originalProfile.phoneNumber ||
        profile.preferredLanguage !== originalProfile.preferredLanguage ||
        profile.birthDate !== originalProfile.birthDate
      )
    );    
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    setProfile((prev) => {
      const updated = {
        ...prev,
        [name]: name === 'birthDate' ? new Date(value).toISOString() : value
      };
  
      if (name === 'language' && value !== prev.language) {
        i18n.changeLanguage(value);
        localStorage.setItem('preferredLanguage', value);
      }
  
      return updated;
    });
  };  

  const handleSave = async () => {
    setLoading(true);
    setMessage('');

    if (!profile.birthDate) {
      setMessage(t('profile.birthDateRequired', 'Please enter your birth date.'));
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(profile)
      });

      const result = await res.json();

      if (res.ok && result.user) {
        setProfile(result.user);
        localStorage.setItem('userData', JSON.stringify(result.user));
        setOriginalProfile(result.user);
        localStorage.setItem('preferredLanguage', result.user.preferredLanguage || 'en');
        if (result.user.preferredLanguage && result.user.preferredLanguage !== i18n.language) {
          i18n.changeLanguage(result.user.preferredLanguage);
        }
        setMessage(t('profile.saveSuccess', 'Profile updated successfully!'));
      } else {
        setMessage(result.message || t('profile.saveFail', 'Update failed.'));
      }
    } catch (err) {
      console.error('Save error:', err);
      setMessage(t('profile.saveError', 'An error occurred while saving.'));
    }
    setLoading(false);
  };

  const handleUpgradeClick = async () => {
    try {
      const res = await fetch(config.getApiUrl('purchases/initiate-user-upgrade'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: profile._id,
          new_user_level: 10,
          amount: 25.00,  // TODO put in database
          language: i18n.language // 🔤 pass language to backend
        }),
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Stripe session error:', err);
      setMessage(t('profile.upgradeError', 'Upgrade failed. Try again later.'));
    }
  };  

  const handleResendVerification = async () => {
    setMessage('');
    try {
      const res = await fetch(config.getApiUrl('auth/resend-verification'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email: profile.email })
      });
  
      const result = await res.json();
      if (res.ok) {
        setMessage(t('profile.verificationSent', 'Verification email sent. Please check your inbox.'));
      } else {
        setMessage(result.message || t('profile.verificationError', 'Failed to send verification email.'));
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      setMessage(t('profile.verificationError', 'Failed to send verification email.'));
    }
  };  


  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-md mx-auto relative">
        {showCloseButton && (
          <button
            onClick={() => navigate('/explorer')}
            className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-xl"
            aria-label="Close"
            title="Close"
          >
            &times;
          </button>
        )}

        <div className="max-w-md mx-auto p-4">
          <h2 className="text-xl font-bold mb-4 text-gray-800">{t('profile.title', 'User Profile')}</h2>

          <label className="text-xs mt-1 text-gray-600 block mb-1">{t('profile.firstName', 'First Name')}</label>
          <input
            type="text"
            name="firstName"
            value={profile.firstName}
            onChange={handleChange}
            placeholder={t('profile.firstName', 'First Name')}
            className="w-full mb-3 p-2 border rounded text-sm"
          />

          <label className="text-xs mt-1 text-gray-600 block mb-1">{t('profile.lastName', 'Last Name')}</label>
          <input
            type="text"
            name="lastName"
            value={profile.lastName}
            onChange={handleChange}
            placeholder={t('profile.lastName', 'Last Name')}
            className="w-full mb-3 p-2 border rounded text-sm"
          />

          <label className="text-xs mt-1 text-gray-600 block mb-1">{t('profile.email', 'Email')}</label>
          <input
            type="email"
            name="email"
            value={profile.email}
            placeholder={t('profile.email', 'Email')}
            className="w-full mb-3 p-2 border rounded text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
            readOnly
            disabled
          />

          {!profile.verified && (
            <div className="mb-4 text-sm text-yellow-700 bg-yellow-100 border border-yellow-300 p-3 rounded">
              <p className="mb-2">{t('profile.unverifiedMessage', 'Your email address is not verified.')}</p>
              <button
                onClick={handleResendVerification}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm"
              >
                {t('profile.resendVerification', 'Resend Verification Email')}
              </button>
            </div>
          )}

          <label className="text-xs mt-1 text-gray-600 block mb-1">{t('profile.phoneNumber', 'Phone Number')}</label>
          <input
            type="text"
            name="phoneNumber"
            value={profile.phoneNumber}
            onChange={handleChange}
            placeholder={t('profile.phoneNumber', 'Phone Number')}
            className="w-full mb-4 p-2 border rounded text-sm"
          />

          <label className="text-xs mt-1 text-gray-600 block mb-1">{t('profile.birthDate', 'Birth Date')}</label>
          <input
            type="date"
            name="birthDate"
            value={profile.birthDate ? profile.birthDate.split('T')[0] : ''}
            onChange={handleChange}
            className="w-full mb-4 p-2 border rounded text-sm"
          />

          <label className="text-xs mt-1 text-gray-600 block mb-1">{t('profile.preferredLanguage', 'Preferred Language')}</label>
          <select
            name="preferredLanguage"
            value={profile.preferredLanguage}
            onChange={handleChange}
            className="w-full mb-4 p-2 border rounded text-sm"
          >
            <option value="en">{t('profile.language.en', 'English')}</option>
            <option value="it">{t('profile.language.it', 'Italiano')}</option>
          </select>

          {typeof profile.userLevel === 'number' && (
            <div className="flex flex-col items-center my-6 space-y-2">
              <span
                className={`
                  inline-block px-4 py-1 rounded-full text-sm font-semibold 
                  border-2 
                  ${
                    profile.userLevel >= 10
                      ? 'text-green-600 border-green-500 bg-green-50'
                      : 'text-gray-600 border-gray-300 bg-gray-100'
                  }
                `}
              >
                {getUserLevelLabel(profile.userLevel, t)}
              </span>

              {profile.userType && (
                <div className="flex items-center space-x-2 mt-1">
                  <img
                    src={`/${profile.userType}.png`}
                    alt={profile.userType}
                    className="h-8 w-8 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t(`profile.userType.${profile.userType}`, profile.userType)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* USING PAYMENT LINKS
          {profile.userLevel === 0 && (
            <button
              onClick={() => setShowModal(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded w-full mb-4"
            >
              {t('profile.upgradeButton', 'Upgrade')}
            </button>
          )}
            */}

          {/* USING API */}
          {profile.userLevel === 0 && (
            <button
              onClick={handleUpgradeClick}
              className="bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded w-full mb-4"
            >
              {t('profile.upgradeButton', 'Upgrade')}
            </button>
          )}

          {hasChanges() && (
            <button
              onClick={handleSave}
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded w-full mb-4"
              disabled={loading}
            >
              {loading ? t('profile.saving', 'Saving...') : t('profile.saveButton', 'Save')}
            </button>
          )}

          {message && <p className="mt-3 text-xs text-center text-gray-700">{message}</p>}

          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded w-full mb-2"
          >
            {t('profile.logoutButton', 'Logout')}
          </button>
        </div>

        <StripePaymentModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          buyButtonId={
            userLanguage === 'it'
              ? 'buy_btn_1RGdUn3fJJOAcp2oiyKqS0Za'
              : 'buy_btn_1RFuYo3fJJOAcp2ovkg6yJHQ'
          }
          publishableKey="pk_test_51RFsHx3fJJOAcp2oFaAFrRt6rVkuf9e8KIeMNJOT6x5tNj8Jl4EfeynqfnAcjSFU89HjfGvYkAFofIiQhMGQJjOV00UJ0oAjGX"
          paymentTitle={t('profile.upgradeTitle', 'Upgrade to Supporter')}
          paymentMessage={t('profile.upgradeMessage', 'Support the project and unlock premium features.')}
        />
      </div>
    </div>
  );
};

export default UserProfile;
